import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { ICON_CELL, ICON_INSET, fade } from './sidebarMotion'

interface SidebarMenuItemProps {
  id: string
  icon: string
  label: string
  isCollapsed: boolean
  isActive?: boolean
  isClicked?: boolean
  onClick?: () => void
  isDarkMode?: boolean
}

const FONT =
  'Rubik, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'
const WHITE_ICON = 'brightness(0) invert(1)'

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  id,
  icon,
  label,
  isCollapsed,
  isActive = false,
  isClicked = false,
  onClick,
  isDarkMode: isDarkModeProp,
}) => {
  const theme = useTheme()
  const isDarkMode = isDarkModeProp ?? theme.palette.mode === 'dark'
  const isHighlighted = isActive || isClicked

  const activeBackground = isDarkMode
    ? '#1D4ED8'
    : theme.palette.sidebar?.active || theme.palette.primary.main
  const hoverBackground = isDarkMode
    ? 'rgba(148,163,184,0.14)'
    : theme.palette.sidebar?.hover || theme.palette.action.hover

  // The label and the icon take the same color from the same state, in both widths.
  const labelColor = isHighlighted
    ? '#FFFFFF'
    : isDarkMode
      ? '#E5E7EB'
      : theme.palette.text.primary
  const iconFilter = isDarkMode || isHighlighted ? WHITE_ICON : 'none'
  const iconSize = 'clamp(18px, 3vh, 28px)'

  return (
    <Box
      data-testid={`sidebar-item-${id}`}
      data-active={isHighlighted ? 'true' : 'false'}
      role="button"
      aria-label={label}
      sx={{
        py: 'clamp(4px, 0.8vh, 8px)',
        pr: ICON_INSET,
        pl: 1.5,
        boxSizing: 'border-box',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: isHighlighted ? activeBackground : 'transparent',
        borderRadius: 1.5,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, color 0.2s ease',
        '&:hover': {
          cursor: isHighlighted ? 'default' : 'pointer',
          backgroundColor: isHighlighted ? activeBackground : hoverBackground,
        },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant="body2"
          aria-hidden={isCollapsed}
          data-testid={`sidebar-label-${id}`}
          sx={{
            fontFamily: FONT,
            fontSize: 'clamp(13px, 1vw, 16px)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            flexShrink: 0,
            maxWidth: '180px',
            color: labelColor,
            ...fade(!isCollapsed),
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: ICON_CELL,
            flexShrink: 0,
          }}
        >
          <img
            src={icon}
            alt=""
            data-testid={`sidebar-icon-${id}`}
            style={{ filter: iconFilter, width: iconSize, height: iconSize }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default SidebarMenuItem
